import { addDays, addMonths, addYears, format, subDays, subMonths, subYears } from 'date-fns';

export type PeriodDirection = 'past' | 'future';

export type PeriodPreset =
  | 'last7'
  | 'last14'
  | 'lastMonth'
  | 'last3Months'
  | 'last6Months'
  | 'lastYear'
  | 'next7'
  | 'next14'
  | 'nextMonth'
  | 'next3Months'
  | 'next6Months'
  | 'nextYear'
  | 'custom';

export interface PeriodFilterValue {
  preset: PeriodPreset | null;
  // Only used when preset === 'custom'.
  startDate: Date | null;
  endDate: Date | null;
}

export const EMPTY_PERIOD_FILTER: PeriodFilterValue = {
  preset: null,
  startDate: null,
  endDate: null,
};

// Preset order matches the design; 'custom' is always last.
const PAST_PRESETS: PeriodPreset[] = [
  'last7',
  'last14',
  'lastMonth',
  'last3Months',
  'last6Months',
  'lastYear',
  'custom',
];

const FUTURE_PRESETS: PeriodPreset[] = [
  'next7',
  'next14',
  'nextMonth',
  'next3Months',
  'next6Months',
  'nextYear',
  'custom',
];

export const periodPresetsFor = (direction: PeriodDirection): PeriodPreset[] =>
  direction === 'past' ? PAST_PRESETS : FUTURE_PRESETS;

// i18n key for a preset label, e.g. 'manage.period.last7'.
export const periodPresetLabelKey = (preset: PeriodPreset): string => `manage.period.${preset}`;

export interface PeriodRange {
  from?: string;
  to?: string;
}

// Resolves a period filter into the bare calendar dates the API expects: `yyyy-MM-dd`, no time and no
// offset, taken in the user's local calendar. Presets are resolved relative to "now" so they always
// reflect the moment the query runs. Returns {} when nothing usable is set (no preset, or a custom
// period missing either date).
//
// Do NOT send an instant here. The server compares whole days against dates stored in UTC and cannot
// know the caller's zone, so `startOfDay(local).toISOString()` shifts every boundary: for a Sydney
// user, 05-Aug local is 04-Aug 14:00Z, which the server reads as the 4th. Sending the date the user
// actually picked keeps their calendar and the filter in agreement.
const toApiDate = (date: Date): string => format(date, 'yyyy-MM-dd');

export const getPeriodRange = (value: PeriodFilterValue | null | undefined): PeriodRange => {
  if (!value?.preset) return {};

  if (value.preset === 'custom') {
    if (!value.startDate || !value.endDate) return {};
    return {
      from: toApiDate(value.startDate),
      to: toApiDate(value.endDate),
    };
  }

  const now = new Date();
  const pastRange = (from: Date): PeriodRange => ({
    from: toApiDate(from),
    to: toApiDate(now),
  });
  const futureRange = (to: Date): PeriodRange => ({
    from: toApiDate(now),
    to: toApiDate(to),
  });

  switch (value.preset) {
    case 'last7':
      return pastRange(subDays(now, 7));
    case 'last14':
      return pastRange(subDays(now, 14));
    case 'lastMonth':
      return pastRange(subMonths(now, 1));
    case 'last3Months':
      return pastRange(subMonths(now, 3));
    case 'last6Months':
      return pastRange(subMonths(now, 6));
    case 'lastYear':
      return pastRange(subYears(now, 1));
    case 'next7':
      return futureRange(addDays(now, 7));
    case 'next14':
      return futureRange(addDays(now, 14));
    case 'nextMonth':
      return futureRange(addMonths(now, 1));
    case 'next3Months':
      return futureRange(addMonths(now, 3));
    case 'next6Months':
      return futureRange(addMonths(now, 6));
    case 'nextYear':
      return futureRange(addYears(now, 1));
    default:
      return {};
  }
};
